import React from 'react';
import { Form, FormControl, InputGroup } from 'react-bootstrap';
import { RecipeTable } from '../../../../enums/RecipeTable';
import { RecipeType } from '../../../../enums/RecipeType';
import { recipeResources } from '../../../../resources/recipeResources';
import { strings } from '../../../../resources/strings';
import { Session } from '../../../../models/Session';
import ServicesContext from '../../../../contexts/ServicesContext';
import { NewOrderRecipeInput } from '../../../../services/Files/NewOrderRecipe/NewOrderRecipeInput';
import { SessionHelper } from '../../../../helpers/SessionHelper';
import { Button } from '../../../../common-components/Button/Button';
import { If } from '../../../../common-components/If/If';
import { FormRecipeValues } from '../FormRecipeV2';
import { Container, FormApproveFieldsRow, FormField, FormFieldsRow } from './styles';

interface ICommonDataProps {
  recipeTable: RecipeTable;
  orderId?: number;
  orderNumber?: string;
  values?: FormRecipeValues;
  change: (values: FormRecipeValues) => void;
  session?: Session | null;
}

const CommonData: React.FC<ICommonDataProps> = ({
  recipeTable,
  orderId,
  orderNumber,
  values,
  change,
  session
}) => {

  const inputFile = React.useRef<any>(null);

  const {
    newOrderRecipeService,
    toastService
  } = React.useContext(ServicesContext);

  const recipeHasAnyDnp =
    values?.farDiagnostic?.leftNasoPupillaryDistance || values?.farDiagnostic?.rightNasoPupillaryDistance ||
    values?.nearDiagnostic?.leftNasoPupillaryDistance || values?.nearDiagnostic?.rightNasoPupillaryDistance;

  const recipeHasAnyInformation =
    values?.farDiagnostic?.leftSpherical || values?.farDiagnostic?.leftCylindrical || values?.farDiagnostic?.leftAxis ||
    values?.farDiagnostic?.rightSpherical || values?.farDiagnostic?.rightCylindrical || values?.farDiagnostic?.rightAxis ||
    values?.nearDiagnostic?.leftSpherical || values?.nearDiagnostic?.leftCylindrical || values?.nearDiagnostic?.leftAxis ||
    values?.nearDiagnostic?.rightSpherical || values?.nearDiagnostic?.rightCylindrical || values?.nearDiagnostic?.rightAxis;

  const userCanManageMultifocalRecipe = SessionHelper.canManageMultifocalRecipe(session) || false;

  return (
    <Container>
      <FormFieldsRow>
        <FormField>
          <InputGroup>
            <InputGroup.Text style={{ minWidth: '145px' }}>{strings.Type}</InputGroup.Text>
            <Form.Select
              aria-label={strings.Type}
              defaultValue={values?.recipeType}
              onChange={(e: any) => changeRecipeType(e.target.value)}
              disabled={!userCanManageMultifocalRecipe}
            >
              <option value={RecipeType.SingleVision}>{recipeResources.SingleVisionRecipeTypeDescription}</option>
              <option value={RecipeType.Bifocal}>{recipeResources.BifocalRecipeTypeDescription}</option>
            </Form.Select>
          </InputGroup>
        </FormField>
        <FormField marginLeft='10px'>
          <InputGroup>
            <InputGroup.Text style={{ width: '145px' }}>{strings.File}</InputGroup.Text>
            {values?.pathToFile ? <a className='form-control' target='_blank' rel='noreferrer' href={values?.pathToFile || ''}>{strings.Recipe}</a> : <div className='form-control'></div>}
            <Button type="secondary" click={openFile} text={strings.Attach} />
            <input type='file' style={{ display: 'none' }} ref={inputFile} onChange={changeFile} />
          </InputGroup>
        </FormField>
      </FormFieldsRow>
      <If condition={recipeTable === RecipeTable.CustomerRecipe}>
        <FormFieldsRow>
          <InputGroup>
            <InputGroup.Text style={{ width: '145px' }}>{strings.Name}</InputGroup.Text>
            <FormControl defaultValue={values?.name} onChange={(e: any) => changeValue('name', e.target.value)} />
          </InputGroup>
        </FormFieldsRow>
        <FormFieldsRow>
          <InputGroup>
            <InputGroup.Text style={{ width: '145px' }}>{strings.Description}</InputGroup.Text>
            <FormControl defaultValue={values?.description} onChange={(e: any) => changeValue('description', e.target.value)} />
          </InputGroup>
        </FormFieldsRow>
      </If>
      <If condition={recipeTable === RecipeTable.Recipe}>
        <FormFieldsRow>
          <InputGroup>
            <InputGroup.Text style={{ width: '145px' }}>{`${strings.Name}/${strings.Description}`}</InputGroup.Text>
            <FormControl defaultValue={values?.name} onChange={(e: any) => changeValue('name', e.target.value)} />
          </InputGroup>
        </FormFieldsRow>
      </If>
      <FormFieldsRow>
        <FormField marginRight='5px'>
          <InputGroup>
            <InputGroup.Text style={{ minWidth: '145px' }}>Altura Pulila OD</InputGroup.Text>
            <FormControl maxLength={2} defaultValue={values?.rightEyeHeight?.toString()} onChange={(e: any) => changeValue('rightEyeHeight', e.target.value)} />
          </InputGroup>
        </FormField>
        <FormField marginLeft='5px'>
          <InputGroup>
            <InputGroup.Text style={{ width: '145px' }}>Altura Pulila OE</InputGroup.Text>
            <FormControl maxLength={2} defaultValue={values?.leftEyeHeight?.toString()} onChange={(e: any) => changeValue('leftEyeHeight', e.target.value)} />
          </InputGroup>
        </FormField>
      </FormFieldsRow>
      <FormApproveFieldsRow>
        <Form.Check
          type='checkbox'
          label={strings.ConfirmRecipe}
          checked={values?.recipeApproved || false}
          onChange={(e: any) => changeRecipeApproved()}
          disabled={!recipeHasAnyInformation}
        />
        <Form.Check
          type='checkbox'
          label={strings.ConfirmDNP}
          checked={values?.nasoPupillaryDistanceApproved || false}
          onChange={() => changeNasoPupillaryDistanceApproved()}
          disabled={!recipeHasAnyDnp}
        />
      </FormApproveFieldsRow>
    </Container>
  );

  function changeValue(field: string, value: any) {
    if (!values) values = new FormRecipeValues();
    (values as any)[field] = value;
    change({ ...values });
  }

  function changeRecipeType(value?: RecipeType) {
    const bifocal = value ? +value === +RecipeType.Bifocal : false;

    if (bifocal && values && !values.multifocalInfo) {
      values.multifocalInfoIsConfirmed = false;
    }

    if (!values) values = new FormRecipeValues();
    values.recipeType = value;
    change({ ...values });
  }

  function changeRecipeApproved() {
    if (!values) values = new FormRecipeValues();
    values.recipeApproved = !values.recipeApproved;
    change({ ...values });
  }

  function changeNasoPupillaryDistanceApproved() {
    if (!values) values = new FormRecipeValues();
    values.nasoPupillaryDistanceApproved = !values.nasoPupillaryDistanceApproved;
    change({ ...values });
  }

  function openFile() {
    inputFile.current.click();
  }

  function changeFile(event: any) {
    event.stopPropagation();
    event.preventDefault();

    const orderIdValue = orderId || 0;
    const orderNumberValue = orderNumber || orderIdValue;
    const recipeIdValue = values?.id || 0;

    var file = event.target.files[0];

    const name = file.name.replace(/\s/g, '_');
    const fileName = `order_${orderNumberValue}_recipe_${recipeIdValue}_file_${name}`;

    const formData = new FormData();
    formData.append('orderId', orderIdValue.toString());
    formData.append(fileName, file, fileName);

    const input = new NewOrderRecipeInput();
    input.formData = formData;

    newOrderRecipeService.handle(input)
      .then(result => {
        if (result?.value?.value) {
          if (!values) values = new FormRecipeValues();
          values.pathToFile = result?.value?.value;
          toastService.success(strings.FileSuccessfullyAttached);
          change({ ...values });
        }
      })
      .catch(() => {
        toastService.warning(strings.OpsSomethingWrongSendTheData);
      });

    inputFile.current.value = '';
  }
}

export { CommonData };